import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ModalBase } from '../modal/ModalBase';
import { Fade } from '../private/components/fade/Fade';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { IconButton } from '../button/IconButton';
import { Typography } from '../typography/Typography';
import { Close } from '../private/icons/Close';
import HTMLElementType from '../private/utils/HTMLElementType';

const min = Math.min;
const max = Math.max;

function within(minVal, value, maxVal) {
  return max(minVal, min(value, maxVal));
}

function getLengthFromAxis(axis) {
  return axis === 'y' ? 'height' : 'width';
}

function getSide(placement) {
  return placement.split('-')[0];
}

function getMainAxisFromPlacement(placement) {
  return ['top', 'bottom'].includes(getSide(placement)) ? 'x' : 'y';
}

const getArrowStyle = (placement, arrowData) => {
  const { x: arrowX, y: arrowY } = arrowData || {};
  const mainPlacement = placement.split('-')[0];
  const staticSide = { top: 'bottom', right: 'left', bottom: 'top', left: 'right' }[mainPlacement];
  // Avoid shadow overlapping by cutting off the excess
  // Coordinates are based on a 8px squared arrow
  const clipPath = {
    bottom: 'path(\'M18-10-10 18A1 1 0 0118-10\')',
    top: 'path(\'M18-10-10 18A1 1 0 0018-10\')',
    left: 'path(\'M18 18-10-10A1 1 0 0118 18\')',
    right: 'path(\'M18 18-10-10A1 1 0 0018 18\')',
  }[mainPlacement];
  return {
    left: arrowX != null ? `${arrowX}px` : '',
    top: arrowY != null ? `${arrowY}px` : '',
    right: '',
    bottom: '',
    [staticSide]: '-4px',
    clipPath
  };
};

const getArrowData = ({
  arrowDimensions,
  coords,
  elementHeight,
  elementWidth,
  paddingObject,
  placement,
  rects
}) => {

  const axis = getMainAxisFromPlacement(placement);
  const length = getLengthFromAxis(axis);

  const minProp = axis === 'y' ? 'top' : 'left';
  const maxProp = axis === 'y' ? 'bottom' : 'right';

  const endDiff = rects.reference[length]
    + rects.reference[axis]
    - coords[axis]
    - rects.floating[length];

  const startDiff = coords[axis] - rects.reference[axis];

  let clientSize = axis === 'y'
    ? elementHeight || 0
    : elementWidth || 0;

  const centerToReference = endDiff / 2 - startDiff / 2;
  const mini = paddingObject[minProp];
  const maxi = clientSize - arrowDimensions[length] - paddingObject[maxProp];
  const center = clientSize / 2 - arrowDimensions[length] / 2 + centerToReference;
  const offset = within(mini, center, maxi);

  return {
    data: {
      [axis]: offset,
      centerOffset: center - offset,
    },
  };
};

const placementMap = {
  top: {
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'center'
    },
    transformOrigin: {
      vertical: 'bottom',
      horizontal: 'center'
    }
  },
  'top-start': {
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'left'
    },
    transformOrigin: {
      vertical: 'bottom',
      horizontal: 'left'
    }
  },
  'top-end': {
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right'
    },
    transformOrigin: {
      vertical: 'bottom',
      horizontal: 'right'
    }
  },
  bottom: {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'center'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'center'
    }
  },
  'bottom-start': {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left'
    }
  },
  'bottom-end': {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'right'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'right'
    }
  },
  left: {
    anchorOrigin: {
      vertical: 'center',
      horizontal: 'left'
    },
    transformOrigin: {
      vertical: 'center',
      horizontal: 'right'
    }
  },
  'left-start': {
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'left'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'right'
    }
  },
  'left-end': {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left'
    },
    transformOrigin: {
      vertical: 'bottom',
      horizontal: 'right'
    }
  },
  right: {
    anchorOrigin: {
      vertical: 'center',
      horizontal: 'right'
    },
    transformOrigin: {
      vertical: 'center',
      horizontal: 'left'
    }
  },
  'right-start': {
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left'
    }
  },
  'right-end': {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'right'
    },
    transformOrigin: {
      vertical: 'bottom',
      horizontal: 'left'
    }
  }
};

function getOffsetTop(rect, vertical) {
  let offset = 0;

  if (typeof vertical === 'number') {
    offset = vertical;
  } else if (vertical === 'center') {
    offset = rect.height / 2;
  } else if (vertical === 'bottom') {
    offset = rect.height;
  }

  return offset;
}

function getOffsetLeft(rect, horizontal) {
  let offset = 0;

  if (typeof horizontal === 'number') {
    offset = horizontal;
  } else if (horizontal === 'center') {
    offset = rect.width / 2;
  } else if (horizontal === 'right') {
    offset = rect.width;
  }

  return offset;
}

function getTransformOriginValue(transformOrigin) {
  return [transformOrigin.horizontal, transformOrigin.vertical]
    .map((n) => (typeof n === 'number' ? `${n}px` : n))
    .join(' ');
}

function resolveAnchorEl(anchorEl) {
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl;
}

function debounce(func, wait = 166) {
  let timeout;
  function debounced(...args) {
    const later = () => {
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  }

  debounced.clear = () => {
    clearTimeout(timeout);
  };

  return debounced;
}

function ownerDocument(node) {
  if (node) {
    return node.ownerDocument;
  }
  return document;
}

function ownerWindow(node) {
  const doc = ownerDocument(node);
  return doc.defaultView || window;
}

const PopoverRoot = React.forwardRef((props, ref) => {

  const {
    placement: placementProp,
    offset,
    style,
    smallScreen = false,
    ...other
  } = props;

  const [placement] = React.useState(placementProp);

  const mainPlacement = placement?.split('-')[0];

  const offsetSizes = {
    bottom: {
      tight: 'sui-translate-y-0',
      snug: 'sui-translate-y-1',
      normal: 'sui-translate-y-2',
      relaxed: 'sui-translate-y-3',
      loose: 'sui-translate-y-4'
    },
    top: {
      tight: 'sui--translate-y-0',
      snug: 'sui--translate-y-1',
      normal: 'sui--translate-y-2',
      relaxed: 'sui--translate-y-3',
      loose: 'sui--translate-y-4'
    },
    right: {
      tight: 'sui-translate-x-0',
      snug: 'sui-translate-x-1',
      normal: 'sui-translate-x-2',
      relaxed: 'sui-translate-x-3',
      loose: 'sui-translate-x-4'
    },
    left: {
      tight: 'sui--translate-x-0',
      snug: 'sui--translate-x-1',
      normal: 'sui--translate-x-2',
      relaxed: 'sui--translate-x-3',
      loose: 'sui--translate-x-4'
    }
  };

  const offsetClasses = smallScreen ? undefined : offsetSizes[mainPlacement]?.[offset];

  const classes = classNames('sui-bg-primary sui-text-primary sui-rounded-base sui-shadow-sm sui-absolute sui-outline-none', offsetClasses);

  return (
    <Fade
      classes={classes}
      ref={ref}
      {...other}
    />
  );
});

const Popover = React.forwardRef((props, ref) => {

  const {
    anchorEl,
    children,
    offset = 'normal',
    open,
    placement = 'bottom',
    title,
    ...other
  } = props;

  const paperRef = React.useRef();
  const arrowRef = React.useRef();

  const matches = useBreakpoint('sm');

  const getAnchorOffset = React.useCallback(() => {
    const { anchorOrigin = { vertical: 'bottom', horizontal: 'center' } } = placementMap[placement] || {};

    const resolvedAnchorEl = resolveAnchorEl(anchorEl);

    const anchorElement = (resolvedAnchorEl && resolvedAnchorEl.nodeType === 1)
      ? resolvedAnchorEl : ownerDocument(paperRef.current).body;

    const anchorRect = anchorElement.getBoundingClientRect();

    return {
      top: anchorRect.top + getOffsetTop(anchorRect, anchorOrigin.vertical),
      left: anchorRect.left + getOffsetLeft(anchorRect, anchorOrigin.horizontal),
      x: anchorRect.x,
      y: anchorRect.y,
      height: anchorRect.height,
      width: anchorRect.width
    };
  }, [anchorEl, placement]);

  const getTransformOrigin = React.useCallback((elemRect) => {
    const { transformOrigin = { vertical: 'top', horizontal: 'center' } } = placementMap[placement] || {};

    return {
      vertical: getOffsetTop(elemRect, transformOrigin.vertical),
      horizontal: getOffsetLeft(elemRect, transformOrigin.horizontal)
    };
  }, [placement]);

  const getPositioningStyle = React.useCallback(
    (element) => {
      // const maxWidth = element.offsetWidth > 400 ? 400 : element.offsetWidth;
      let calculatedWidth;
      if (element.offsetWidth < 228) calculatedWidth = 228;
      else if (element.offsetWidth > 400) calculatedWidth = 400;
      else calculatedWidth = element.offsetWidth;
      const elemRect = {
        width: calculatedWidth,
        height: element.offsetHeight,
      };

      if (matches.lesser) {
        return {
          left: '0px',
          right: '0px',
          bottom: '0px',
          width: '100%'
        };
      }

      const elemTransformOrigin = getTransformOrigin(elemRect);

      const anchorOffset = getAnchorOffset();
      let top = anchorOffset.top - elemTransformOrigin.vertical;
      let left = anchorOffset.left - elemTransformOrigin.horizontal;
      const bottom = top + elemRect.height;
      const right = left + elemRect.width;

      const containerWindow = ownerWindow(resolveAnchorEl(anchorEl));

      const marginThreshold = 16;

      const heightThreshold = containerWindow.innerHeight - marginThreshold;
      const widthThreshold = containerWindow.innerWidth - marginThreshold;

      if (top < marginThreshold) {
        const diff = top - marginThreshold;
        top -= diff;
        elemTransformOrigin.vertical += diff;
      } else if (bottom > heightThreshold) {
        const diff = bottom - heightThreshold;
        top -= diff;
        elemTransformOrigin.vertical += diff;
      }

      if (left < marginThreshold) {
        const diff = left - marginThreshold;
        left -= diff;
        elemTransformOrigin.horizontal += diff;
      } else if (right > widthThreshold) {
        const diff = right - widthThreshold;
        left -= diff;
        elemTransformOrigin.horizontal += diff;
      }

      const arrow = getArrowData({
        arrowDimensions: {
          height: 8,
          width: 8
        },
        coords: {
          x: left,
          y: top
        },
        elementHeight: elemRect.height,
        elementWidth: elemRect.width,
        paddingObject: {
          bottom: 0,
          left: 0,
          right: 0,
          top: 0
        },
        placement,
        rects: {
          floating: {
            height: elemRect.height,
            width: elemRect.width,
            x: 0,
            y: 0,
          },
          reference: {
            height: anchorOffset.height,
            width: anchorOffset.width,
            x: anchorOffset.x,
            y: anchorOffset.y
          }
        }
      });

      const arrowStyles = getArrowStyle(placement, arrow.data);

      return {
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        transformOrigin: getTransformOriginValue(elemTransformOrigin),
        minWidth: '228px',
        maxWidth: '400px',
        arrowLeft: arrowStyles.left,
        arrowTop: arrowStyles.top,
        arrowRight: arrowStyles.right,
        arrowBottom: arrowStyles.bottom,
        arrowClipPath: arrowStyles.clipPath
      };
      // Missing `placement` and `matches.lesser` which is fine.
      // We don't support changing those props.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchorEl, getAnchorOffset, getTransformOrigin]);

  const setPositioningStyles = React.useCallback(() => {
    const element = paperRef.current;
    const arrow = arrowRef.current;

    if (!element) {
      return;
    }

    const positioning = getPositioningStyle(element);

    if (positioning.width !== undefined) {
      element.style.width = positioning.width;
    }
    if (positioning.minWidth !== undefined) {
      element.style.minWidth = positioning.minWidth;
    }
    if (positioning.maxWidth !== undefined) {
      element.style.maxWidth = positioning.maxWidth;
    }
    if (positioning.top !== undefined) {
      element.style.top = positioning.top;
    }
    if (positioning.left !== undefined) {
      element.style.left = positioning.left;
    }
    if (positioning.right !== undefined) {
      element.style.right = positioning.right;
    }
    if (positioning.bottom !== undefined) {
      element.style.bottom = positioning.bottom;
    }
    if (positioning.transformOrigin !== undefined) {
      element.style.transformOrigin = positioning.transformOrigin;
    }
    if (positioning.arrowLeft !== undefined) {
      arrow.style.left = positioning.arrowLeft;
    }
    if (positioning.arrowTop !== undefined) {
      arrow.style.top = positioning.arrowTop;
    }
    if (positioning.arrowRight !== undefined) {
      arrow.style.right = positioning.arrowRight;
    }
    if (positioning.arrowBottom !== undefined) {
      arrow.style.bottom = positioning.arrowBottom;
    }
    if (positioning.arrowClipPath !== undefined) {
      arrow.style.clipPath = positioning.arrowClipPath;
    }
  }, [getPositioningStyle]);

  const handleEntering = () => {
    setPositioningStyles();
  };

  React.useEffect(() => {
    if (open) {
      setPositioningStyles();
    }
  });

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleResize = debounce(() => {
      setPositioningStyles();
    });

    const containerWindow = ownerWindow(anchorEl);
    containerWindow.addEventListener('resize', handleResize);
    return () => {
      handleResize.clear();
      containerWindow.removeEventListener('resize', handleResize);
    };
  }, [anchorEl, open, setPositioningStyles]);

  const container = anchorEl ? ownerDocument(anchorEl).body : undefined;

  return (
    <ModalBase
      container={container}
      open={open}
      ref={ref}
      componentsProps={{
        backdrop: {
          invisible: true
        }
      }}
      {...other}
    >
      <PopoverRoot
        offset={offset}
        placement={placement}
        smallScreen={matches.lesser}
        ref={paperRef}
        in={open}
        onEnter={handleEntering}
      >
        <div className="sui-flex sui-w-full sui-text-left sui-flex-col sui-gap-2 sui-p-3">
          <div className="sui-flex-auto sui-text-left">
            <div className="sui-flex sui-items-start sui-relative sui-items-center sui-gap-4">
              <span className="sui-grow">
                <Typography variant="body-base" weight="bold">{title}</Typography>
              </span>
              <span>
                <IconButton
                  aria-label="close"
                  onClick={props.onClose}
                  icon={Close}
                />
              </span>
            </div>
          </div>
          {children}
        </div>
        {!matches.lesser ? (
          <div
            ref={arrowRef}
            className="sui-absolute sui-w-2 sui-h-2 sui-rotate-45 sui-bg-primary sui-shadow-sm"
          />
        ) : null}
      </PopoverRoot>
    </ModalBase>
  );
});

Popover.displayName = 'Popover';

Popover.propTypes = {
  /**
   * An HTML element used to set the position of the popover.
   */
  anchorEl: PropTypes.oneOfType([HTMLElementType, PropTypes.func]),
  /**
   * The Popover title
   */
  title: PropTypes.string,
  /**
   * The Popover content
   */
  children: PropTypes.node,
  /**
   * If `true`, the Popover is visible
   */
  open: PropTypes.bool.isRequired,
  /**
   * An event handler to be executed once the Popover is closed.
   */
  onClose: PropTypes.func,
  /**
   * The prefered placement of the Tooltip component relative to the trigger object.
   * Note that the final placement could be different depending on available space and trigger location.
   */
  placement: PropTypes.oneOf([
    'bottom',
    'bottom-start',
    'bottom-end',
    'top',
    'top-start',
    'top-end',
    'left',
    'left-start',
    'left-end',
    'right',
    'right-start',
    'right-end'
  ]),
  /**
   * Specify how far the popover should be from the bounding box of the trigger object
   */
  offset: PropTypes.oneOf([
    'tight',
    'snug',
    'normal',
    'relaxed',
    'loose',
  ]),
};

Popover.defaultProps = {
};

export { Popover };